import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AllFCTransferItemsTransformConfig } from 'components/warehouse/components/fcTransfers/allTransfers/allFCTransferItemsFilterConfig';
import { AllFCTransferItemsTable } from 'components/warehouse/components/fcTransfers/allTransfers/AllFCTransferItemsTable';
import { AllFCTransfersFilters } from 'components/warehouse/components/fcTransfers/allTransfers/AllFCTransfersFilters';
import { useFCTransferItemsSearch } from 'components/warehouse/components/fcTransfers/allTransfers/fcTransferItemsQueries';
import { fromUrl, pushToUrl, removeEmptyFilters, toApi } from 'shared';

const ALL_FC_TRANSFERS_LIST_PATH = '/warehouse/all-fc-transfers';
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * @param {string} searchString
 * @returns {Object}
 */
function buildStateFromSearch(searchString) {
    const parsed = fromUrl(searchString, AllFCTransferItemsTransformConfig);
    const pageSize = parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE;
    return { ...parsed, pageSize };
}

/**
 * Merch all-FC transfers: line-item focused, view-only list
 * (`GET /inventory/v1/fc-transfers/items`).
 */
export function ViewAllFCTransfers() {
    const history = useHistory();
    const location = useLocation();

    const [filters, setFilters] = useState(() =>
        buildStateFromSearch(typeof window !== 'undefined' ? window.location.search : ''),
    );
    const [currentCursor, setCurrentCursor] = useState(undefined);
    const [cursorHistory, setCursorHistory] = useState([]);
    const storedTotalCountRef = useRef(undefined);

    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;

    useEffect(() => {
        const liveSearch =
            (typeof window !== 'undefined' && window.location.search ? window.location.search : '') ||
            (typeof location.search === 'string' ? location.search : '');
        setFilters(buildStateFromSearch(liveSearch));
        setCursorHistory([]);
        setCurrentCursor(undefined);
        storedTotalCountRef.current = undefined;
    }, [location.search, location.pathname]);

    const apiFilters = useMemo(() => {
        const transformed = toApi(filters, AllFCTransferItemsTransformConfig);
        if (!transformed.pageSize) {
            transformed.pageSize = DEFAULT_PAGE_SIZE;
        }
        return transformed;
    }, [filters]);

    const paginatedFilters = useMemo(
        () => ({
            ...apiFilters,
            cursor: currentCursor,
            ...(currentCursor !== undefined &&
                storedTotalCountRef.current !== undefined && {
                    totalCount: storedTotalCountRef.current,
                }),
        }),
        [apiFilters, currentCursor],
    );

    const paginatedQuery = useFCTransferItemsSearch(paginatedFilters);

    useEffect(() => {
        if (paginatedQuery.data?.pagination?.totalCount !== undefined) {
            storedTotalCountRef.current = paginatedQuery.data.pagination.totalCount;
        }
    }, [paginatedQuery.data?.pagination?.totalCount]);

    const { data, error, isFetching, isLoading } = paginatedQuery;
    const rows = useMemo(() => data?.rows ?? [], [data]);
    const pagination = useMemo(() => data?.pagination ?? {}, [data]);
    const totalCount = pagination.totalCount ?? storedTotalCountRef.current ?? 0;
    const errorMessage = error?.message ?? '';

    const getFiltersForBackNav = useCallback(() => {
        const fromWindow = typeof window !== 'undefined' && window?.location?.search ? window.location.search : '';
        const fromRouter = typeof location?.search === 'string' && location.search ? location.search : '';
        return fromWindow || fromRouter || '';
    }, [location.search]);

    const handleFilterChange = useCallback(
        (nextFilters) => {
            const updatedState = {
                ...nextFilters,
                pageSize: filters.pageSize || DEFAULT_PAGE_SIZE,
            };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(updatedState);
            pushToUrl(removeEmptyFilters(updatedState), AllFCTransferItemsTransformConfig, {
                replace: false,
            });
        },
        [filters.pageSize],
    );

    const handleNextPage = useCallback(() => {
        if (pagination.hasMore && pagination.nextCursor) {
            setCursorHistory((prev) => [...prev, currentCursor]);
            setCurrentCursor(pagination.nextCursor);
        }
    }, [pagination, currentCursor]);

    const handlePreviousPage = useCallback(() => {
        if (cursorHistory.length > 0) {
            const newHistory = [...cursorHistory];
            const previousCursor = newHistory.pop();
            setCursorHistory(newHistory);
            setCurrentCursor(previousCursor);
        }
    }, [cursorHistory]);

    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            const nextState = { ...filters, pageSize: newPageSize };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(nextState), AllFCTransferItemsTransformConfig, {
                replace: true,
            });
        },
        [filters],
    );

    const handleViewTransfer = useCallback(
        (row) => {
            const id = row?.transferId;
            if (id === undefined || id === null) return;
            history.push(`/warehouse/fc-transfers/${encodeURIComponent(String(id))}`, {
                fcTransfersFilters: getFiltersForBackNav(),
                fcTransfersListPath: ALL_FC_TRANSFERS_LIST_PATH,
                viewOnly: true,
            });
        },
        [getFiltersForBackNav, history],
    );

    const filterValuesForUI = useMemo(() => {
        const { pageSize: _ps, ...filterOnly } = filters;
        return filterOnly;
    }, [filters]);

    const currentPage = cursorHistory.length + 1;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <h1 className="text-xl font-semibold">All FC Transfers</h1>
                <p className="mt-1 text-sm text-muted-foreground">Line items across all facilities (view only)</p>
                <div className="mt-4">
                    <AllFCTransfersFilters
                        key={location.search}
                        initialValues={filterValuesForUI}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>
            <div className="flex flex-1 flex-col px-4 pb-6 pt-4">
                {errorMessage ? (
                    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {errorMessage}
                    </div>
                ) : null}
                <AllFCTransferItemsTable
                    currentPage={currentPage}
                    data={rows}
                    emptyMessage="No FC transfer items found."
                    hasNextPage={pagination.hasMore}
                    hasPrevious={cursorHistory.length > 0}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    onNext={handleNextPage}
                    onPageSizeChange={handlePageSizeChange}
                    onPrevious={handlePreviousPage}
                    onViewTransfer={handleViewTransfer}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    totalCount={totalCount}
                    totalPages={totalPages}
                />
            </div>
        </div>
    );
}

ViewAllFCTransfers.displayName = 'ViewAllFCTransfers';
